import React, { useState } from 'react';
import { ChevronDown, HelpCircle, Search } from 'lucide-react';
import { faqData } from '../mockData';
import CTA from '../components/CTA';

const FAQ = () => {
  const [openId, setOpenId] = useState(null);

  const toggleFAQ = (id) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <div>
      {/* Creative Hero Section */}
      <div className="relative bg-gradient-to-br from-purple-600 via-purple-700 to-pink-700 py-40 mt-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'radial-gradient(circle at 30% 40%, white 2px, transparent 2px), radial-gradient(circle at 70% 60%, white 2px, transparent 2px)',
          backgroundSize: '80px 80px'
        }}></div>
        <div className="absolute top-20 left-10 w-64 h-64 bg-white/10 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-pink-400/20 rounded-full blur-3xl animate-float" style={{animationDelay: '1s'}}></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center space-x-3 bg-white/10 backdrop-blur-sm rounded-full px-6 py-3 mb-8">
            <HelpCircle size={20} className="text-purple-200" />
            <span className="text-white font-bold text-sm">Help Center</span>
          </div>
          <h1 className="text-6xl md:text-7xl lg:text-8xl font-black text-white mb-6 leading-none">
            <span className="block">Frequently</span>
            <span className="block text-purple-200">Asked</span>
            <span className="block">Questions</span>
          </h1>
          <p className="text-2xl text-purple-100 max-w-3xl mx-auto leading-relaxed">
            Find answers to common questions about our services
          </p>
        </div>
      </div>

      {/* FAQ Section - Creative Accordion */}
      <section className="py-32 bg-gradient-to-b from-white via-purple-50 to-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-purple-100 rounded-full opacity-20 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-pink-100 rounded-full opacity-20 blur-3xl"></div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-6">
            {faqData.map((faq, index) => {
              const isOpen = openId === faq.id;
              
              return (
                <div
                  key={faq.id}
                  className="group relative"
                >
                  {/* Creative Card Design */}
                  <div className={`bg-white rounded-3xl overflow-hidden shadow-lg border-2 transition-all duration-500 ${
                    isOpen 
                      ? 'border-purple-600 shadow-2xl' 
                      : 'border-gray-100 hover:border-purple-300 hover:shadow-xl'
                  }`}>
                    <button
                      onClick={() => toggleFAQ(faq.id)}
                      className="w-full flex items-center justify-between p-8 text-left hover:bg-purple-50 transition-all duration-300"
                    >
                      {/* Question Number Badge */}
                      <div className="flex items-start space-x-4 flex-grow">
                        <div className={`flex-shrink-0 w-12 h-12 rounded-2xl flex items-center justify-center font-black text-lg transition-all duration-300 ${
                          isOpen 
                            ? 'bg-gradient-to-br from-purple-600 to-pink-600 text-white' 
                            : 'bg-purple-100 text-purple-600 group-hover:bg-purple-200'
                        }`}>
                          {String(index + 1).padStart(2, '0')}
                        </div>
                        <span className={`text-xl font-bold pr-8 transition-colors duration-300 ${
                          isOpen ? 'text-purple-600' : 'text-gray-900 group-hover:text-purple-600'
                        }`}>
                          {faq.question}
                        </span>
                      </div>
                      
                      {/* Animated Icon */}
                      <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300 ${
                        isOpen 
                          ? 'bg-purple-600 rotate-180' 
                          : 'bg-gray-100 group-hover:bg-purple-100'
                      }`}>
                        <ChevronDown size={24} className={isOpen ? 'text-white' : 'text-gray-400 group-hover:text-purple-600'} />
                      </div>
                    </button>
                    
                    {/* Answer with Smooth Transition */}
                    <div className={`overflow-hidden transition-all duration-500 ${
                      isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                    }`}>
                      <div className="px-8 pb-8 pl-24">
                        <div className="pt-4 border-t-2 border-purple-100">
                          <p className="text-lg text-gray-700 leading-relaxed">
                            {faq.answer}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Decorative Glow on Hover */}
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-purple-500/0 to-pink-500/0 group-hover:from-purple-500/5 group-hover:to-pink-500/5 transition-all duration-300 pointer-events-none -z-10"></div>
                </div>
              );
            })}
          </div>

          {/* Additional Help Section - Creative Card */}
          <div className="mt-20 bg-gradient-to-br from-purple-600 to-pink-600 rounded-3xl p-12 shadow-2xl text-center text-white relative overflow-hidden">
            {/* Decorative Elements */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>
            <div className="absolute bottom-0 left-0 w-40 h-40 bg-white/10 rounded-full blur-2xl"></div>
            
            <div className="relative z-10">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-3xl mb-6">
                <Search size={32} className="text-white" />
              </div>
              <h3 className="text-4xl font-black mb-4">
                Still Have Questions?
              </h3>
              <p className="text-xl text-purple-100 mb-8 max-w-2xl mx-auto">
                Our team is here to help. Get in touch with us for personalized assistance.
              </p>
              <a
                href="/contact"
                className="inline-block bg-white text-purple-600 px-10 py-5 rounded-2xl font-black text-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
              >
                Contact Our Team
              </a>
            </div>
          </div>
        </div>
      </section>

      <CTA />
    </div>
  );
};

export default FAQ;
